import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, XCircle, Link as LinkIcon, Unlink, TrendingUp, DollarSign, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Accounting = () => {
  const { selectedProperty } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [connecting, setConnecting] = useState(null);

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [providersRes, historyRes, statsRes] = await Promise.all([
        axios.get(`${API}/accounting/providers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/accounting/sync/history?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/accounting/sync/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setProviders(providersRes.data);
      setSyncHistory(historyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching accounting data:', error);
      toast.error('Failed to load accounting data');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider) => {
    try {
      setConnecting(provider);
      const token = localStorage.getItem('access_token');
      
      await axios.post(`${API}/accounting/connect/${provider}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Connected to ${provider === 'quickbooks' ? 'QuickBooks' : 'Xero'}!`);
      fetchData();
    } catch (error) {
      toast.error('Failed to connect');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!confirm(`Disconnect from ${provider === 'quickbooks' ? 'QuickBooks' : 'Xero'}?`)) return;
    
    try {
      const token = localStorage.getItem('access_token');
      
      await axios.post(`${API}/accounting/disconnect/${provider}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Disconnected successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const getSyncStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-emerald-600 bg-emerald-50 border-emerald-200';
      case 'failed':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const getSyncTypeIcon = (type) => {
    switch (type) {
      case 'invoice':
        return <Receipt size={16} />;
      case 'payment':
        return <DollarSign size={16} />;
      case 'expense':
        return <TrendingUp size={16} />;
      default:
        return <Receipt size={16} />;
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        Loading...
      </div>
    );
  }

  const connectedProvider = providers.find(p => p.is_connected);

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Accounting Integrations</h1>
          <p className="text-slate-600 mt-1">Connect QuickBooks or Xero to sync financial data</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && connectedProvider && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Syncs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.total_syncs}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Success Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">{stats.success_rate}%</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Failed Syncs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{stats.failed_syncs}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Provider Connection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {providers.map((provider) => (
          <Card key={provider.provider} className={provider.is_connected ? 'border-emerald-200' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    provider.provider === 'quickbooks' ? 'bg-green-100' : 'bg-blue-100'
                  }`}>
                    <span className="text-2xl font-bold">{provider.provider === 'quickbooks' ? 'QB' : 'X'}</span>
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {provider.provider === 'quickbooks' ? 'QuickBooks Online' : 'Xero'}
                    </CardTitle>
                    {provider.is_connected && provider.company_name && (
                      <p className="text-sm text-slate-600 mt-1">{provider.company_name}</p>
                    )}
                  </div>
                </div>
                
                {provider.is_connected ? (
                  <CheckCircle2 className="text-emerald-500" size={24} />
                ) : (
                  <XCircle className="text-slate-300" size={24} />
                )}
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              {provider.is_connected ? (
                <>
                  <div className="text-sm text-slate-600">
                    <p>Connected: {format(new Date(provider.connected_at), 'PPp')}</p>
                    {provider.last_sync && (
                      <p>Last Sync: {format(new Date(provider.last_sync), 'PPp')}</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDisconnect(provider.provider)}
                      className="gap-2"
                    >
                      <Unlink size={14} />
                      Disconnect
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    {provider.provider === 'quickbooks' 
                      ? 'Sync reservations, payments, and expenses with QuickBooks Online'
                      : 'Connect to Xero for automated accounting sync'
                    }
                  </p>
                  
                  <Button
                    onClick={() => handleConnect(provider.provider)}
                    disabled={connecting === provider.provider || connectedProvider}
                    className="w-full gap-2"
                  >
                    {connecting === provider.provider ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <LinkIcon size={16} />
                        Connect to {provider.provider === 'quickbooks' ? 'QuickBooks' : 'Xero'}
                      </>
                    )}
                  </Button>
                  
                  {connectedProvider && (
                    <p className="text-xs text-amber-600 text-center">
                      Disconnect {connectedProvider.provider} first to connect to {provider.provider}
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sync History */}
      {connectedProvider && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Sync Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {syncHistory.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <p>No sync activity yet</p>
                <p className="text-sm mt-2">
                  Create a reservation or record a payment to see automatic sync
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {syncHistory.map((record) => (
                  <div
                    key={record.id}
                    className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg"
                  >
                    <div className={`p-2 rounded-lg ${
                      record.status === 'success' ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      {getSyncTypeIcon(record.sync_type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900 capitalize">
                          {record.sync_type}
                        </span>
                        <Badge className={getSyncStatusColor(record.status)}>
                          {record.status}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-slate-600">{record.message}</p>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span>{format(new Date(record.synced_at), 'PPp')}</span>
                        {record.external_id && (
                          <span>ID: {record.external_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      {!connectedProvider && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <div className="text-blue-600 mt-1">
                <LinkIcon size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Why Connect Accounting Software?</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Automatically sync reservations as invoices</li>
                  <li>• Record payments as revenue entries</li>
                  <li>• Track property expenses and maintenance costs</li>
                  <li>• Generate accurate financial reports</li>
                  <li>• Simplify tax filing and bookkeeping</li>
                </ul>
                <p className="text-xs text-blue-700 mt-3">
                  <strong>Note:</strong> This is a mock integration for demonstration. 
                  In production, you'll need to provide actual OAuth credentials.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Accounting;
